export const PEP_HEALTH_RESULT_PREFIX = 'PEP_HC_v1'
export const PEP_HEALTH_RESULT_PREFIX_V2 = 'PEP_HC_v2'
export const PEP_HEALTH_UNKNOWN_STATE = 'Unknown'

/** Keys match receiver/API `badgeKey`; labels for Health Check PMS filters and UI. */
export const PEP_HEALTH_BADGE_FILTER_OPTIONS = {
  Working: 'Working',
  NeedsAttention: 'Needs attention',
  AppNotInstalled: 'App not installed',
  PepAuthorizationError: 'PEP authorization error'
}

export const PEP_HEALTH_ONBOARDING_FILTER_OPTIONS = {
  Done: 'Onboarding done',
  Pending: 'Onboarding pending',
  [PEP_HEALTH_UNKNOWN_STATE]: 'Onboarding unknown'
}

export const PEP_HEALTH_FUTURE_FILTER_OPTIONS = {
  Active: 'Future active',
  Inactive: 'Future inactive',
  [PEP_HEALTH_UNKNOWN_STATE]: 'Future unknown'
}

export const PEP_HEALTH_COMBINED_FILTER_OPTIONS = {
  'badge:Working': 'Health: Working',
  'badge:NeedsAttention': 'Health: Needs attention',
  'badge:AppNotInstalled': 'Health: App not installed',
  'badge:PepAuthorizationError': 'Health: PEP authorization error',
  'onboarding:Done': 'Onboarding: Done',
  'onboarding:Pending': 'Onboarding: Pending',
  'onboarding:Unknown': 'Onboarding: Unknown',
  'future:Active': 'Future: Active',
  'future:Inactive': 'Future: Inactive',
  'future:Unknown': 'Future: Unknown'
}

const BADGE_LABELS = {
  AppNotInstalled: 'App not installed',
  PepAuthorizationError: 'PEP authorization error',
  Working: 'Working',
  NeedsAttention: 'Needs attention'
}

const ONBOARDING_LABELS = {
  Done: 'Done',
  Pending: 'Pending'
}

const FUTURE_LABELS = {
  Active: 'Active',
  Inactive: 'Inactive'
}

const KNOWN_ONBOARDING_STATES = ['Done', 'Pending']
const KNOWN_FUTURE_STATES = ['Active', 'Inactive']

/**
 * Parses LastResult from pms-receiver PEP checkups when it uses the PEP_HC_v1 pipe format.
 * @param {string | null | undefined} lastResult
 * @returns {{ badgeKey: string, badgeLabel: string, activeDevices: number, issueDevices: number, raw: string } | null}
 */
export function parsePepHealthCheckLastResult(lastResult) {
  if (!lastResult || typeof lastResult !== 'string') {
    return null
  }
  const isV1 = lastResult.startsWith(`${PEP_HEALTH_RESULT_PREFIX}|`)
  const isV2 = lastResult.startsWith(`${PEP_HEALTH_RESULT_PREFIX_V2}|`)
  if (!isV1 && !isV2) {
    return null
  }
  const parts = lastResult.split('|')
  if (parts.length < 4) {
    return null
  }
  const badgeKey = parts[1]
  const activeDevices = parseInt(parts[2], 10)
  const issueDevices = parseInt(parts[3], 10)
  if (Number.isNaN(activeDevices) || Number.isNaN(issueDevices)) {
    return null
  }
  const badgeLabel = BADGE_LABELS[badgeKey] || badgeKey
  const onboardingState =
    isV2 && parts[4] ? parts[4] : null
  const futureState =
    isV2 && parts[5] ? parts[5] : null
  return {
    badgeKey,
    badgeLabel,
    activeDevices,
    issueDevices,
    onboardingState,
    onboardingLabel: onboardingState ? ONBOARDING_LABELS[onboardingState] || onboardingState : null,
    futureState,
    futureLabel: futureState ? FUTURE_LABELS[futureState] || futureState : null,
    raw: lastResult
  }
}

/**
 * PEP rows always get a health line: parsed pipe string from API, or "App not installed" when there is no telemetry payload.
 * @param {string | null | undefined} lastResult
 * @param {string | undefined} pms
 * @returns {{ badgeKey: string, badgeLabel: string, activeDevices: number, issueDevices: number, raw: string, isNoTelemetryFallback?: boolean } | null}
 */
export function getPepHealthCheckDisplay(lastResult, pms, reportsInLast48Hours = 0) {
  if (pms !== 'pep') {
    return null
  }
  const hasRecentPmsHistoryData = Number(reportsInLast48Hours) > 0
  const parsed = parsePepHealthCheckLastResult(lastResult)
  if (parsed) {
    // If data is still flowing in the last 48 hours, keep hotel status as working.
    // Non-working signals are still reflected via issueDevices (needs attention count).
    if (hasRecentPmsHistoryData) {
      const derivedIssueSignals = [
        parsed.badgeKey !== 'Working',
        parsed.onboardingState === 'Pending',
        parsed.futureState === 'Inactive'
      ].filter(Boolean).length
      return {
        ...parsed,
        badgeKey: 'Working',
        badgeLabel: BADGE_LABELS.Working,
        issueDevices: Math.max(parsed.issueDevices, derivedIssueSignals)
      }
    }
    return parsed
  }
  if (hasRecentPmsHistoryData) {
    return {
      badgeKey: 'Working',
      badgeLabel: BADGE_LABELS.Working,
      activeDevices: 0,
      issueDevices: 1,
      onboardingState: null,
      onboardingLabel: null,
      futureState: null,
      futureLabel: null,
      raw: typeof lastResult === 'string' ? lastResult : '',
      isNoTelemetryFallback: true
    }
  }
  return {
    badgeKey: 'AppNotInstalled',
    badgeLabel: BADGE_LABELS.AppNotInstalled,
    activeDevices: 0,
    issueDevices: 0,
    onboardingState: null,
    onboardingLabel: null,
    futureState: null,
    futureLabel: null,
    raw: typeof lastResult === 'string' ? lastResult : '',
    isNoTelemetryFallback: true
  }
}

/**
 * Matches parsed PEP display details against selected Health Check filters.
 * Empty arrays mean "no filter".
 */
export function matchesPepHealthFilters(
  pepDisplay,
  { badgeKeys = [], onboardingStates = [], futureStates = [] } = {}
) {
  if (!pepDisplay) {
    return false
  }

  const matchesBadge =
    !badgeKeys || badgeKeys.length === 0 || badgeKeys.includes(pepDisplay.badgeKey)
  const onboardingHasUnknownFilter =
    Array.isArray(onboardingStates) && onboardingStates.includes(PEP_HEALTH_UNKNOWN_STATE)
  const onboardingFilterIsBroad =
    Array.isArray(onboardingStates) &&
    KNOWN_ONBOARDING_STATES.every(state => onboardingStates.includes(state))
  const matchesOnboarding =
    !onboardingStates ||
    onboardingStates.length === 0 ||
    onboardingFilterIsBroad ||
    (pepDisplay.onboardingState && onboardingStates.includes(pepDisplay.onboardingState)) ||
    (!pepDisplay.onboardingState && onboardingHasUnknownFilter)
  const futureHasUnknownFilter =
    Array.isArray(futureStates) && futureStates.includes(PEP_HEALTH_UNKNOWN_STATE)
  const futureFilterIsBroad =
    Array.isArray(futureStates) &&
    KNOWN_FUTURE_STATES.every(state => futureStates.includes(state))
  const matchesFuture =
    !futureStates ||
    futureStates.length === 0 ||
    futureFilterIsBroad ||
    (pepDisplay.futureState && futureStates.includes(pepDisplay.futureState)) ||
    (!pepDisplay.futureState && futureHasUnknownFilter)

  return matchesBadge && matchesOnboarding && matchesFuture
}

/**
 * Hotel dashboard user roster count from Health Check sync (excludes internal global users).
 * @param {object | null | undefined} row
 * @returns {number | null}
 */
export function getPepDashboardUserCount(row) {
  const raw = row?.pepDashboardUserCount ?? row?.PepDashboardUserCount
  if (raw == null || raw === '') {
    return null
  }

  const count = Number(raw)
  return Number.isFinite(count) && count > 0 ? count : null
}

/**
 * Count of hotel users with at least one registered PEP desktop device/signal.
 * @param {object | null | undefined} row
 * @param {{ activeDevices?: number, issueDevices?: number } | null | undefined} pepDisplay
 */
export function getPepUsersWithAppCount(row, pepDisplay) {
  const fromDisplay =
    (Number(pepDisplay?.activeDevices) || 0) + (Number(pepDisplay?.issueDevices) || 0)
  const deviceCount = Number(row?.pepDesktopAppDeviceCount ?? row?.PepDesktopAppDeviceCount ?? 0)
  const signalBasedCount = deviceCount > 0 ? deviceCount : fromDisplay

  const distinctRaw = row?.pepDesktopAppDistinctUserCount ?? row?.PepDesktopAppDistinctUserCount
  if (distinctRaw == null || distinctRaw === '') {
    return signalBasedCount
  }

  const distinct = Number(distinctRaw)
  if (!Number.isFinite(distinct) || distinct < 0) {
    return signalBasedCount
  }

  if (distinct > 0) {
    return distinct
  }

  // Ignore a zero distinct-user count when PEP health still reports installed devices.
  return signalBasedCount > 0 ? signalBasedCount : 0
}

/**
 * True when some hotel users are set up but others still need the PEP desktop app or sign-in.
 * @param {{ activeDevices?: number, issueDevices?: number } | null | undefined} pepDisplay
 * @param {object | null | undefined} row
 */
export function isPepPartialSetup(pepDisplay, row = null) {
  if (!pepDisplay) {
    return false
  }

  const activeDevices = Number(pepDisplay.activeDevices) || 0
  const issueDevices = Number(pepDisplay.issueDevices) || 0

  if (activeDevices > 0 && issueDevices > 0) {
    return true
  }

  const dashboardUserCount = getPepDashboardUserCount(row)
  const usersWithAppCount = getPepUsersWithAppCount(row, pepDisplay)

  if (
    dashboardUserCount != null &&
    usersWithAppCount > 0 &&
    usersWithAppCount < dashboardUserCount
  ) {
    return true
  }

  return false
}

/**
 * Human-readable last result for CSV / exports when the value is a PEP health pipe string.
 * @param {string | null | undefined} lastResult
 * @param {string | undefined} pms
 */
export function formatPepHealthCheckLastResultForExport(lastResult, pms, reportsInLast48Hours = 0) {
  if (pms !== 'pep') {
    return lastResult || ''
  }
  const display = getPepHealthCheckDisplay(lastResult, pms, reportsInLast48Hours)
  if (!display) {
    return lastResult || ''
  }
  const onboardingPart = display.onboardingLabel
    ? `, onboarding ${display.onboardingLabel}`
    : ''
  const futurePart = display.futureLabel
    ? `, future ${display.futureLabel}`
    : ''
  return `${display.badgeLabel} (PEP active ${display.activeDevices}, issues ${display.issueDevices}${onboardingPart}${futurePart})`
}
